import type {
  SecurityFeedResponse,
  SecurityOutcome,
  SecuritySeverity,
  SecuritySource,
} from "@xop/dashboard-shared";

/**
 * Typed fetch wrapper for the dashboard's /api/admin/security feed.
 * Threads the Authorization header from the calling component, mirroring
 * the AI Providers / Otto Bots panels' api.ts.
 */
export interface SecurityFeedParams {
  sinceHours?: number;
  minSeverity?: SecuritySeverity;
  outcome?: SecurityOutcome;
  source?: SecuritySource;
  limit?: number;
}

async function request<T>(
  path: string,
  authHeader?: string,
): Promise<T> {
  const headers = new Headers();
  headers.set("Accept", "application/json");
  if (authHeader) headers.set("Authorization", authHeader);

  const res = await fetch(`/api/admin/security${path}`, { headers });
  const text = await res.text();
  if (!res.ok) {
    let body: unknown;
    try {
      body = JSON.parse(text);
    } catch {
      body = text;
    }
    const msg =
      typeof body === "object" && body !== null && "error" in body
        ? String((body as { error: unknown }).error)
        : `Request failed: ${res.status}`;
    throw new Error(msg);
  }
  return JSON.parse(text) as T;
}

export const securityApi = {
  feed: (params: SecurityFeedParams = {}, authHeader?: string) => {
    const qs = new URLSearchParams();
    if (params.sinceHours != null) qs.set("sinceHours", String(params.sinceHours));
    if (params.minSeverity) qs.set("minSeverity", params.minSeverity);
    if (params.outcome) qs.set("outcome", params.outcome);
    if (params.source) qs.set("source", params.source);
    if (params.limit != null) qs.set("limit", String(params.limit));
    const suffix = qs.toString() ? `?${qs.toString()}` : "";
    return request<SecurityFeedResponse>(suffix, authHeader);
  },
};
